import React from 'react';

const getTopPerformer = (players, statColumn, findMin = false) => {
  if (!players || !Array.isArray(players)) return null;
  
  let topPlayer = null;
  let extremeValue = findMin ? Infinity : -Infinity;
  
  for (const player of players) {
    const value = parseFloat(player[statColumn]) || 0;
    if (findMin ? value < extremeValue : value > extremeValue) {
      extremeValue = value;
      topPlayer = player;
    }
  }
  
  return topPlayer;
};

const getStatIcon = (column) => {
  const icons = {
    'YRDS': '📊',
    'TD': '🏈',
    'CMP%': '🎯',
    'ATT': '🏃',
    'TDS': '🏈',
    'REC': '🤚',
    'TCK': '🛡️',
    'SCK': '⚡',
    'INT': '🔄',
    'FGM': '⚽',
    'FG%': '📈',
    'SAD': '🚫',
    'SAS': '🚫',
  };
  return icons[column] || '⭐';
};

const getCardColor = (column) => {
  const colors = {
    'YRDS': '#4A90E2',
    'TD': '#7FBA00',
    'CMP%': '#9C27B0',
    'ATT': '#FF9800',
    'TDS': '#E81B23',
    'REC': '#00ACC1',
    'TCK': '#757575',
    'SCK': '#F57C00',
    'INT': '#5E35B1',
    'FGM': '#C2185B',
    'FG%': '#01579B',
    'SAD': '#D32F2F',
    'SAS': '#D32F2F',
  };
  return colors[column] || '#2196F3';
};

const TopPerformerCard = ({ label, icon, color, player, statValue, statColumn, isBlocking = false }) => {
  if (!player) {
    return (
      <div className="top-performer-card" style={{ borderLeftColor: color }}>
        <div className="card-content">
          <div className="stat-label">{label}</div>
          <div className="no-data">No data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="top-performer-card" style={{ borderLeftColor: color }}>
      <div className="card-icon" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="card-content">
        <div className="stat-label">{label}</div>
        <div className="player-name">{isBlocking ? player.TEAM : (player.NAME || 'Unknown')}</div>
        <div className="player-info">
          {isBlocking && player.ABV && <span>{player.ABV}</span>}
          {!isBlocking && player.TEAM && <span>{player.TEAM}</span>}
          {!isBlocking && player.POS && <span>• {player.POS}</span>}
        </div>
        <div className="stat-value">{statValue}</div>
      </div>
    </div>
  );
};

const TopPerformersCards = ({ stats, selectedStatType, blockingMetric = 'SAD' }) => {
  if (!stats || Object.keys(stats).length === 0) {
    return <div className="no-stats-message">No statistics available</div>;
  }

  const currentStats = stats[selectedStatType.toLowerCase()] || [];
  if (!currentStats || currentStats.length === 0) {
    return <div className="no-stats-message">No statistics available for {selectedStatType}</div>;
  }

  const metricConfigs = {
    PASSING: [
      { column: 'YRDS', label: 'Passing Yards' },
      { column: 'TD', label: 'Passing TDs' },
      { column: 'CMP%', label: 'Completion %' },
    ],
    RUSHING: [
      { column: 'YRDS', label: 'Rushing Yards' },
      { column: 'TDS', label: 'Rushing TDs' },
      { column: 'ATT', label: 'Rushing Attempts' },
    ],
    RECEIVING: [
      { column: 'REC', label: 'Receptions' },
      { column: 'YRDS', label: 'Receiving Yards' },
      { column: 'TDS', label: 'Receiving TDs' },
    ],
    DEFENSE: [
      { column: 'TCK', label: 'Total Tackles' },
      { column: 'SCK', label: 'Sacks' },
      { column: 'INT', label: 'Interceptions' },
    ],
    KICKING: [
      { column: 'FGM', label: 'Field Goals Made' },
      { column: 'FG%', label: 'Field Goal %' },
      { column: 'PNTS', label: 'Total Points' },
    ],
  };

  let metrics = metricConfigs[selectedStatType.toUpperCase()] || [];
  
  if (selectedStatType.toUpperCase() === 'BLOCKING') {
    metrics = [
      { column: blockingMetric, label: blockingMetric === 'SAS' ? 'Lowest Sacks Allowed/Season' : 'Lowest Sacks Allowed/Drive', findMin: true }
    ];
  }

  return (
    <div className="top-performers-section">
      <h2>Top Performers</h2>
      <div className="top-performers-grid">
        {metrics.map((metric) => {
          const topPlayer = getTopPerformer(currentStats, metric.column, metric.findMin || false);
          const statValue = topPlayer ? (parseFloat(topPlayer[metric.column]) || 0) : 0;
          const color = getCardColor(metric.column);
          const isBlocking = selectedStatType.toUpperCase() === 'BLOCKING';

          return (
            <TopPerformerCard
              key={metric.column}
              label={metric.label}
              icon={getStatIcon(metric.column)}
              color={color}
              player={topPlayer}
              statValue={statValue}
              statColumn={metric.column}
              isBlocking={isBlocking}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TopPerformersCards;
